import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Toast from '../../utils/toast';
import {
    getCurrentRole,
    getLoginDataForRole,
    getDashboardPath,
    getLoginPath,
    checkRoleConflict
} from '../../utils/sessionManager';

/**
 * Enhanced AuthGuard with role-based access control
 * Prevents users from accessing host pages and vice versa
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if authorized
 * @param {string} props.role - One of 'ADMIN', 'HOST', or 'USER'
 * @param {boolean} props.guestAllowed - If true, guests can access this USER route
 */
const AuthGuard = ({ children, role, guestAllowed = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isChecking, setIsChecking] = useState(true);

    useEffect(() => {
        const checkAuth = () => {
            // Validate role parameter
            if (!['ADMIN', 'HOST', 'USER'].includes(role)) {
                console.error(`AuthGuard: Invalid role "${role}"`);
                setIsChecking(false);
                return;
            }

            // Check for role conflicts
            const { hasConflict, currentRole } = checkRoleConflict(role);

            if (hasConflict) {
                // User is logged in with a different role
                console.warn(
                    `AuthGuard: Role conflict detected. ` +
                    `Current role: ${currentRole}, Required role: ${role}`
                );

                // Show user-friendly error message
                Toast.error(
                    `You are logged in as ${currentRole}. ` +
                    `Please logout to access ${role} pages.`
                );

                // Redirect to their actual dashboard
                const dashboardPath = getDashboardPath(currentRole);
                navigate(dashboardPath, { replace: true });
                setIsChecking(false);
                return;
            }

            // Check if user is logged in with the correct role
            const loginData = getLoginDataForRole(role);

            if (!loginData) {
                // If this is a USER route, check if Guest Mode is enabled
                if (role === 'USER') {
                    const guest = localStorage.getItem('guestMode') === 'true';
                    if (guest && guestAllowed) {
                        console.log("AuthGuard: Guest Mode access granted for USER route");
                        setIsAuthorized(true);
                        setIsChecking(false);
                        return;
                    }
                }

                // Not logged in with the required role
                console.warn(
                    `AuthGuard: No valid session for ${role}. ` +
                    `Redirecting to login.`
                );

                const loginPath = getLoginPath(role);
                navigate(loginPath, { replace: true });
                setIsChecking(false);
                return;
            }

            // User is authorized
            console.log(`AuthGuard: Access granted for ${role}`);
            setIsAuthorized(true);
            setIsChecking(false);
        };

        checkAuth();
        
        // Listen for storage changes in other tabs/windows
        const handleStorageChange = (e) => {
            if (e.key === 'userLoginData' || e.key === 'hostLoginData' || e.key === 'adminLoginData') {
                checkAuth();
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [role, navigate, location.pathname]);

    // Show loading state while checking authentication
    if (isChecking) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                fontSize: '18px',
                color: '#666',
                flexDirection: 'column',
                gap: '10px'
            }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div>Verifying authentication...</div>
            </div>
        );
    }

    // Only render children if authorized
    return isAuthorized ? <>{children}</> : null;
};

export default AuthGuard;
